import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {Button, Flex, Form, InputNumber, message, Table} from "antd";
import MyText from "../../../../../components/UI/Text/MyText";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import FormItem from "antd/es/form/FormItem";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import {getJournal} from "../../../../../store/slices/TeacherGroupSlice";
import JournalService from "../../../../../services/JournalService";
import {createStyles} from "antd-style";

const useStyle = createStyles(({css, token}) => {
    const {antCls} = token;
    return {
        customTable: css`
            ${antCls}-table {
                ${antCls}-table-container {
                    ${antCls}-table-body,
                    ${antCls}-table-content {
                        scrollbar-width: thin;
                        scrollbar-color: #eaeaea transparent;
                        scrollbar-gutter: stable;
                    }
                }
            }
        `,
    };
});
const GradesPage = () => {
    const {id} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {journal, isLoading} = useSelector(state => state.teacherGroup);

    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([]);


    // Установка начальных значений для формы
    useEffect(() => {
        if (!journal) return;

        const initialValues = {};
        journal.grades?.forEach(grade => {
            initialValues[`grade-${grade.id}`] = grade.grade;
        });

        journal.projects?.forEach(project => {
            project.grades?.forEach(pGrade => {
                initialValues[`project-${pGrade.id}`] = pGrade.grade;
            });
        });

        journal.tests?.forEach(test => {
            test.grades?.forEach(tGrade => {
                initialValues[`test-${tGrade.id}`] = tGrade.grade;
            });
        });

        journal.moduleTests?.forEach(test => {
            test.grades?.forEach(tGrade => {
                initialValues[`moduleTest-${tGrade.id}`] = tGrade.grade;
            });
        });

        journal.finalTests?.forEach(finalTest => {
            initialValues[`finalTest-${finalTest.id}`] = finalTest.grade;
        });

        form.setFieldsValue(initialValues);
    }, [journal]);

    useEffect(() => {
        if (!journal) return;

        setDataSource(
            journal?.group?.students?.map((student, index) => {
                const gradeObj = journal.grades?.find(g => g.user?.id === student.id);
                const finalTestGrade = journal.finalTests?.find(g => g.user?.id === student.id);
                const base = {
                    key: String(index + 1),
                    name: student?.profile?.fullName || '',
                    studentId: student.id,
                    gradeId: gradeObj?.id,
                    grade: gradeObj?.grade ?? 0,
                    finalTestGrade: finalTestGrade?.grade ?? 0,
                    finalTestId: finalTestGrade?.id,
                    tests: [],
                    projects: [],
                };

                journal.projects?.forEach(project => {
                    const studentProjectGrade = project.grades?.find(g => g.user?.id === student.id);
                    base.projects.push(
                        {
                            id: studentProjectGrade?.id,
                            grade: studentProjectGrade?.grade ?? 0,
                            projectId: project.id
                        }
                    )
                });

                journal.tests?.forEach(test => {
                    const studentTestGrade = test.grades?.find(g => g.user?.id === student.id);
                    base.tests.push(
                        {
                            id: studentTestGrade?.id,
                            grade: studentTestGrade?.grade ?? 0,
                            testId: test.id
                        }
                    )
                });

                // Добавляем модульные тесты в массив tests
                journal.moduleTests?.forEach(test => {
                    const studentTestGrade = test.grades?.find(g => g.user?.id === student.id);
                    base.tests.push(
                        {
                            id: studentTestGrade?.id,
                            grade: studentTestGrade?.grade ?? 0,
                            testId: test.id
                        }
                    )
                });

                return base;
            })
        )
    }, [journal]);

    // Базовая колонка с ФИО
    const baseColumn = {
        title: <MyText center><IntlMessage id={"fullName"}/></MyText>,
        width: 200,
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
    };

    // Функция для извлечения уровня из названия (Level 1, Level 2, Level 3)
    const extractLevel = (name) => {
        const match = name.match(/Level (\d+)/i);
        return match ? parseInt(match[1]) : 999; // 999 для элементов без уровня
    };

    // Создаем и сортируем колонки по уровням
    const projectColumns = journal?.projects?.map(project => ({
        title: (
            <Flex style={{cursor: "pointer"}} align={"center"} justify={"center"} gap={"small"}>
                <MyText center>{project.name} {" (" + project.maxGrade + ")"}</MyText>
            </Flex>
        ),
        key: `project-${project.id}`,
        width: 150,
        level: extractLevel(project.name),
        type: 'project',
        render: (record) => (
            <FormItem
                name={`project-${record?.projects.find(pr => pr.projectId === project.id)?.id}`}
                style={{marginBottom: 0}}
                initialValue={record?.projects.find(pr => pr.testId === project.id)?.grade}
                rules={[{required: true, message: <IntlMessage id={"tg.emptyInput"}/>}]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    })).sort((a, b) => a.level - b.level) || [];

    const testColumns = journal?.tests?.map(test => ({
        title: (
            <Flex style={{cursor: "pointer"}} align={"center"} justify={"center"} gap={"small"}>
                <MyText center>{test.name} {" (" + test.maxGrade + ")"}</MyText>
            </Flex>
        ),
        key: `test-${test.id}`,
        width: 150,
        level: extractLevel(test.name),
        type: 'test',
        render: (record) => (
            <FormItem
                name={`test-${record?.tests.find(t => t.testId === test.id)?.id}`}
                style={{marginBottom: 0}}
                initialValue={record?.tests.find(t => t.testId === test.id)?.grade}
                rules={[{required: true, message: <IntlMessage id={"tg.emptyInput"}/>}]}
            >
                {/*{console.log(record)}*/}
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    })).sort((a, b) => a.level - b.level) || [];

    const moduleTestsColumns = journal?.moduleTests?.map(test => ({
        title: (
            <Flex style={{cursor: "pointer"}} align={"center"} justify={"center"} gap={"small"}>
                <MyText center>{test.name} {" (" + test.maxGrade + ")"}</MyText>
            </Flex>
        ),
        key: `moduleTest-${test.id}`,
        width: 150,
        level: extractLevel(test.name),
        type: 'moduleTest',
        render: (record) => (
            <FormItem
                name={`moduleTest-${record?.tests.find(t => t.testId === test.id)?.id}`}
                style={{marginBottom: 0}}
                initialValue={record?.tests.find(t => t.testId === test.id)?.grade}
                rules={[{required: true, message: <IntlMessage id={"tg.emptyInput"}/>}]}
            >
                {/*{console.log(record)}*/}
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    })).sort((a, b) => a.level - b.level) || [];

    // Объединяем и сортируем колонки по уровням
    const sortedColumns = [...projectColumns, ...testColumns, ...moduleTestsColumns].sort((a, b) => {
        // Сначала по уровню
        if (a.level !== b.level) {
            return a.level - b.level;
        }
        // Внутри одного уровня: проекты, тесты, модульные тесты
        const typeOrder = {project: 0, test: 1, moduleTest: 2};
        return typeOrder[a.type] - typeOrder[b.type];
    });

    const shouldShowFinalTest = journal?.group?.students?.some(student => {
        const finalTestGrade = journal.finalTests?.find(g => g.user?.id === student.id);
        return finalTestGrade?.grade !== undefined && finalTestGrade?.grade !== null;
    });

    const isMathCourse = journal?.group?.category?.nameEng === 'Mathematics'

    const finalTestColumn = {
        title: <MyText center><IntlMessage id={"tg.finalGrade"}/></MyText>,
        width: 150,
        key: 'finalTest',
        render: (_, record) => (
            <FormItem
                name={`finalTest-${record.finalTestId}`}
                style={{marginBottom: 0}}
                initialValue={record.finalTestGrade}
                rules={[
                    {
                        required: true,
                        message: <IntlMessage id={"tg.emptyInput"}/>,
                    }
                ]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    };

    const finalTestColumnForMath = {
        title: <MyText center><IntlMessage id={"tg.finalGrade"}/></MyText>,
        width: 150,
        key: 'finalTest',
        render: (_, record) => (
            <FormItem
                name={`finalTest-${record.studentId}`}
                style={{marginBottom: 0}}
                rules={[
                    {
                        required: true,
                        message: <IntlMessage id={"tg.emptyInput"}/>,
                    }
                ]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    };

    const gradeColumn = {
        title: <MyText center><IntlMessage id={"tg.grade"}/></MyText>,
        width: 150,
        dataIndex: 'grade',
        key: 'grade',
        fixed: 'right',
        render: (_, record) => (
            <FormItem
                name={`grade-${record.gradeId}`}
                style={{marginBottom: 0}}
                initialValue={record.grade}
                rules={[
                    {
                        required: true,
                        message: <IntlMessage id={"tg.emptyInput"}/>,
                    }
                ]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    };

    const columns = [baseColumn, ...sortedColumns, ...(shouldShowFinalTest ? [finalTestColumn] : (isMathCourse ? [finalTestColumnForMath] : [])), gradeColumn];

    const convertGradesToHundred = () => {
        const values = form.getFieldsValue();
        const convertedValues = {};

        // Хардкод значений maxGrade
        const PROJECT_MAX_GRADE = 25;
        const TEST_MAX_GRADE = 35;
        const MODULE_TEST_MAX_GRADE = 50;
        const FINAL_TEST_MAX_GRADE = 50;

        for (const [key, value] of Object.entries(values)) {
            if (value === null || value === undefined || value === '') {
                convertedValues[key] = value;
                continue;
            }

            const numValue = Number(value);

            if (key.startsWith('project-')) {
                // Конвертируем только если значение <= maxGrade проекта
                if (numValue > 0 && numValue <= PROJECT_MAX_GRADE) {
                    convertedValues[key] = Math.round((numValue / PROJECT_MAX_GRADE) * 100);
                } else {
                    convertedValues[key] = value;
                }
            } else if (key.startsWith('test-')) {
                // Конвертируем только если значение <= maxGrade теста
                if (numValue > 0 && numValue <= TEST_MAX_GRADE) {
                    convertedValues[key] = Math.round((numValue / TEST_MAX_GRADE) * 100);
                } else {
                    convertedValues[key] = value;
                }
            } else if (key.startsWith('moduleTest-')) {
                // Конвертируем только если значение <= maxGrade модульного теста
                if (numValue > 0 && numValue <= MODULE_TEST_MAX_GRADE) {
                    convertedValues[key] = Math.round((numValue / MODULE_TEST_MAX_GRADE) * 100);
                } else {
                    convertedValues[key] = value;
                }
            } else if (key.startsWith('finalTest-')) {
                // Конвертируем только если значение <= maxGrade финального теста
                if (numValue > 0 && numValue <= FINAL_TEST_MAX_GRADE) {
                    convertedValues[key] = Math.round((numValue / FINAL_TEST_MAX_GRADE) * 100);
                } else {
                    convertedValues[key] = value;
                }
            } else {
                convertedValues[key] = value;
            }
        }

        form.setFieldsValue(convertedValues);
        message.success(<IntlMessage id="success"/>, 3);
    };

    const saveGrades = async () => {
        const values = form.getFieldsValue();
        const gradeMap = {};
        const projectGradeMap = {};
        const testGradeMap = {};
        const moduleTestGradeMap = {};
        const finalTestGradeMap = {};

        for (const [key, value] of Object.entries(values)) {
            if (key.startsWith('project-')) {
                const id = key.replace('project-', '');
                projectGradeMap[id] = value;
            }
            if (key.startsWith('test-')) {
                const id = key.replace('test-', '');
                testGradeMap[id] = value;
            }
            if (key.startsWith('moduleTest-')) {
                const id = key.replace('moduleTest-', '');
                moduleTestGradeMap[id] = value;
            }
            if (key.startsWith('finalTest-')) {
                const id = key.replace('finalTest-', '');
                finalTestGradeMap[id] = value;
            }
        }

        // Обработка итоговой оценки для General English
        if (journal?.group?.category?.nameEng === 'General English') {
            journal?.group?.students?.forEach(student => {
                // const studentTests = journal.tests?.flatMap(test =>
                //     test.grades?.filter(g => g.user.id === student.id) || []
                // );
                //
                // const testSum = studentTests.reduce((sum, g) => sum + (g.grade ?? 0), 0);
                // const avgTest = testSum / studentTests.length;
                const studentTestGrades = journal.tests?.flatMap(test =>
                    test.grades?.filter(g => g.user.id === student.id).map(g => g.id) || []
                );

                const testValues = studentTestGrades.map(id => testGradeMap[id] ?? 0);
                const testSum = testValues.reduce((sum, val) => sum + val, 0);

                const attendanceCount = journal.lessons
                    ?.filter(l => l.date !== null)
                    .reduce((sum, lesson) => {
                        const att = lesson.attendances.find(a => a.user.id === student.id);
                        return sum + (att?.isPresent ? 1 : 0);
                    }, 0) ?? 0;


                const attendancePercent = (attendanceCount * 100) / journal?.lessons?.length;
                const attendanceScore = attendancePercent * 0.4;
                const finalGrade = (testSum * 0.6) + attendanceScore;
                const gradeObj = journal.grades?.find(g => g.user.id === student.id);
                if (gradeObj?.id) {
                    gradeMap[gradeObj.id] = Math.round(finalGrade);
                }
            });
        } else if (journal?.group?.category?.nameEng === 'Mathematics') {
            journal?.group?.students?.forEach(student => {
                const finalTestValue = finalTestGradeMap[student.id] ?? 0;
                const attendanceCount = journal.lessons
                    ?.filter(l => l.date !== null)
                    .reduce((sum, lesson) => {
                        const att = lesson.attendances.find(a => a.user.id === student.id);
                        return sum + (att?.isPresent ? 1 : 0);
                    }, 0) ?? 0;

                const attendancePercent = (attendanceCount * 100) / journal?.lessons?.length;
                const attendanceScore = attendancePercent * 0.6;

                const finalGrade = (finalTestValue * 0.4) + attendanceScore;
                const gradeObj = journal.grades?.find(g => g.user.id === student.id);
                if (gradeObj?.id) {
                    gradeMap[gradeObj.id] = Math.round(finalGrade);
                }
            });
        } else {
            if (journal?.group?.category?.nameEng === 'Foundation' && journal?.group?.course?.nameEng === 'General English') {
                journal?.group?.students?.forEach(student => {
                    // Тесты: 60% (12 тестов, каждый по 5%)
                    const studentTestGrades = journal.tests?.flatMap(test =>
                        test.grades?.filter(g => g.user.id === student.id).map(g => g.id) || []
                    );
                    const testValues = studentTestGrades.map(id => testGradeMap[id] ?? 0);
                    const testAvg = testValues.reduce((sum, val) => sum + val, 0) / testValues.length;
                    const testScore = testAvg * 0.6;

                    // Проекты: 20% (6 проектов)
                    const studentProjectGrades = journal.projects?.flatMap(project =>
                        project.grades?.filter(g => g.user.id === student.id).map(g => g.id) || []
                    );
                    const projectValues = studentProjectGrades.map(id => projectGradeMap[id] ?? 0);
                    const projectAvg = projectValues.reduce((sum, val) => sum + val, 0) / projectValues.length;
                    const projectScore = projectAvg * 0.2;

                    // Модульные тесты: 20% (3 теста)
                    const studentModuleTestValues = journal.moduleTests?.flatMap(test => (test.grades ?? [])
                        .filter(g => g.user.id === student.id)
                        .map(g => g.grade ?? 0) // берем число, если grade может быть null — подставляем 0 или skip
                    ) ?? [];
                    const studentFinalTestGrade = journal.finalTests?.find(g => g.user.id === student.id)?.grade;
                    const moduleFinalTestValues = studentFinalTestGrade != null ? [...studentModuleTestValues, studentFinalTestGrade] : studentModuleTestValues;
                    const moduleTestAvg = moduleFinalTestValues.length > 0 ? moduleFinalTestValues.reduce((s, v) => s + v, 0) / moduleFinalTestValues.length : 0;
                    const moduleTestScore = moduleTestAvg * 0.2;

                    const finalGrade = testScore + projectScore + moduleTestScore;
                    const gradeObj = journal.grades?.find(g => g.user.id === student.id);
                    if (gradeObj?.id) {
                        gradeMap[gradeObj.id] = Math.round(finalGrade);
                    }
                });
            } else {
                // Для остальных категорий и курсов
                for (const [key, value] of Object.entries(values)) {
                    if (key.startsWith('grade-')) {
                        const id = key.replace('grade-', '');
                        gradeMap[id] = value;
                    }
                }
            }

        }

        try {
            const isMathCategory = journal?.group?.category?.nameEng === 'Mathematics';

            const saveGradeTasks = isMathCategory
                ? [
                    JournalService.saveGrades(gradeMap),
                    JournalService.saveGrades(finalTestGradeMap)
                ]
                : [
                    JournalService.saveGrades(projectGradeMap),
                    JournalService.saveGrades(testGradeMap),
                    JournalService.saveGrades(moduleTestGradeMap),
                    JournalService.saveGrades(finalTestGradeMap),
                    JournalService.saveGrades(gradeMap),
                ];

            await Promise.all(saveGradeTasks);

            message.success(<IntlMessage id="success"/>, 5);
        } catch {
            message.error(<IntlMessage id="error"/>, 5);
        } finally {
            form.resetFields();
            dispatch(getJournal({id}));
        }
    };


    return (
        <Flex style={{width: '100%'}} gap={20} vertical>
            <BlockLoading isLoading={isLoading}/>
            <Form form={form}>
                <Table
                    bordered
                    pagination={false}
                    columns={columns}
                    dataSource={dataSource}
                    scroll={{x: 'max-content'}}
                    size="small"
                />
            </Form>
            <Flex gap={10}>
                {
                    journal?.group?.category?.nameEng === 'Foundation' && journal?.group?.course?.nameEng === 'General English' &&
                    <Button onClick={convertGradesToHundred}>
                        <IntlMessage id={'tg.convertTo100'}/>
                    </Button>
                }
                <Button type="primary" onClick={saveGrades}>
                    <IntlMessage id={'save'}/>
                </Button>
            </Flex>
        </Flex>
    );
};

export default GradesPage;
